import React, { useEffect, useMemo, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { useTranslation } from 'react-i18next';
import { addMonths, format, isSameDay, isSameMonth, startOfMonth } from 'date-fns';
import Icon from '../../components/Icon';
import { dateLocale, monthMatrix, weekdayLabels } from '../calendarUtils';

// Шаг 30 минут — как в референсе.
const TIME_OPTIONS = Array.from({ length: 48 }, (_, i) => {
  const hours = String(Math.floor(i / 2)).padStart(2, '0');
  const minutes = i % 2 === 0 ? '00' : '30';
  return `${hours}:${minutes}`;
});

const DURATION_VALUES = [15, 30, 45, 60, 90, 120, 180, 1440];

const roundToHalfHour = (date) => {
  const d = new Date(date);
  d.setMinutes(d.getMinutes() < 30 ? 0 : 30, 0, 0);
  return d;
};

// Универсальный выпадающий список в стиле модалки.
const Select = ({ value, options, onChange, renderValue, className = '' }) => {
  const [open, setOpen] = useState(false);
  const ref = useRef(null);

  useEffect(() => {
    if (!open) return undefined;
    const onClickOutside = (e) => {
      if (ref.current && !ref.current.contains(e.target)) setOpen(false);
    };
    document.addEventListener('mousedown', onClickOutside);
    return () => document.removeEventListener('mousedown', onClickOutside);
  }, [open]);

  const selected = options.find((option) => option.value === value);

  return (
    <div className={`cal-select ${className}`.trim()} ref={ref}>
      <button type="button" className="cal-select-trigger" onClick={() => setOpen((v) => !v)}>
        {renderValue ? renderValue(selected) : <span>{selected?.label ?? '—'}</span>}
        <Icon name="chevron-down" size={14} />
      </button>

      {open && (
        <div className="cal-select-menu">
          {options.map((option) => (
            <button
              key={option.value}
              type="button"
              className={`cal-select-option${option.value === value ? ' active' : ''}`}
              onClick={() => {
                onChange(option.value);
                setOpen(false);
              }}
            >
              {option.color && <span className="cal-dot" style={{ background: option.color }} />}
              <span className="cal-select-option-label">{option.label}</span>
              {option.value === value && <Icon name="check" size={14} />}
            </button>
          ))}
        </div>
      )}
    </div>
  );
};

// Компактный выбор даты — календарь в поповере под полем.
const DateField = ({ value, onChange, locale }) => {
  const [open, setOpen] = useState(false);
  const [month, setMonth] = useState(() => startOfMonth(value));
  const ref = useRef(null);

  useEffect(() => {
    if (!open) return undefined;
    setMonth(startOfMonth(value));
    const onClickOutside = (e) => {
      if (ref.current && !ref.current.contains(e.target)) setOpen(false);
    };
    document.addEventListener('mousedown', onClickOutside);
    return () => document.removeEventListener('mousedown', onClickOutside);
  }, [open, value]);

  return (
    <div className="cal-select" ref={ref}>
      <button type="button" className="cal-select-trigger" onClick={() => setOpen((v) => !v)}>
        <span>{format(value, 'd MMMM yyyy', { locale: dateLocale(locale) })}</span>
        <Icon name="chevron-down" size={14} />
      </button>

      {open && (
        <div className="cal-select-menu cal-date-popover">
          <div className="cal-mini-head">
            <button type="button" className="cal-icon-btn" onClick={() => setMonth(addMonths(month, -1))}>
              <Icon name="chevron-left" size={16} />
            </button>
            <span className="cal-mini-title">
              {format(month, 'LLLL yyyy', { locale: dateLocale(locale) })}
            </span>
            <button type="button" className="cal-icon-btn" onClick={() => setMonth(addMonths(month, 1))}>
              <Icon name="chevron-right" size={16} />
            </button>
          </div>

          <div className="cal-mini-grid">
            {weekdayLabels(locale).map((label) => (
              <span className="cal-mini-weekday" key={label}>{label.slice(0, 2)}</span>
            ))}
            {monthMatrix(month).map((day) => (
              <button
                key={day.toISOString()}
                type="button"
                className={`cal-mini-day${!isSameMonth(day, month) ? ' outside' : ''}${isSameDay(day, value) ? ' selected' : ''}`}
                onClick={() => {
                  onChange(day);
                  setOpen(false);
                }}
              >
                {format(day, 'd')}
              </button>
            ))}
          </div>
        </div>
      )}
    </div>
  );
};

/**
 * Создание и просмотр события: тип, дата, время, длительность, участники.
 * Данные пока живут в состоянии страницы календаря.
 */
const EventDialog = ({ open, event, defaultDate, groups, locale = 'ru', onClose, onSave, onDelete }) => {
  const { t } = useTranslation();
  const [form, setForm] = useState(null);
  const [attendeeDraft, setAttendeeDraft] = useState('');

  const durations = useMemo(
    () => DURATION_VALUES.map((value) => ({ value, label: t(`calendar.dialog.durations.${value}`) })),
    [t]
  );

  const calendarOptions = useMemo(
    () => groups.flatMap((group) => group.calendars.map((calendar) => ({
      value: calendar.id,
      label: calendar.name,
      color: calendar.color,
    }))),
    [groups]
  );

  useEffect(() => {
    if (!open) return;
    const start = event
      ? new Date(event.start)
      : roundToHalfHour(new Date(defaultDate || new Date()));
    if (!event) start.setHours(10, 0, 0, 0);
    const end = event?.end ? new Date(event.end) : new Date(start.getTime() + 60 * 60 * 1000);
    const durationMinutes = Math.max(15, Math.round((end - start) / 60000));

    setForm({
      title: event?.title || '',
      calendarId: event?.calendarId || calendarOptions[0]?.value || '',
      date: start,
      time: format(start, 'HH:mm'),
      duration: DURATION_VALUES.includes(durationMinutes) ? durationMinutes : 60,
      allDay: durationMinutes >= 1440,
      reminder: event?.reminder ?? true,
      location: event?.location || '',
      attendees: event?.attendees || [],
      description: event?.description || '',
    });
    setAttendeeDraft('');
  }, [open, event, defaultDate, calendarOptions]);

  useEffect(() => {
    const onKey = (e) => {
      if (e.key === 'Escape') onClose();
    };
    if (open) document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [open, onClose]);

  if (!open || !form) return null;

  const set = (key, value) => setForm((prev) => ({ ...prev, [key]: value }));
  const activeColor = calendarOptions.find((option) => option.value === form.calendarId)?.color;

  const addAttendee = () => {
    const name = attendeeDraft.trim();
    if (!name || form.attendees.includes(name)) return;
    set('attendees', [...form.attendees, name]);
    setAttendeeDraft('');
  };

  const submit = (e) => {
    e.preventDefault();
    if (!form.title.trim()) return;

    const start = new Date(form.date);
    if (form.allDay) {
      start.setHours(0, 0, 0, 0);
    } else {
      const [hours, minutes] = form.time.split(':').map(Number);
      start.setHours(hours, minutes, 0, 0);
    }
    const minutes = form.allDay ? 1440 : form.duration;
    const end = new Date(start.getTime() + minutes * 60000);

    onSave({
      id: event?.id,
      title: form.title.trim(),
      calendarId: form.calendarId,
      start: start.toISOString(),
      end: end.toISOString(),
      allDay: form.allDay,
      reminder: form.reminder,
      location: form.location.trim(),
      attendees: form.attendees,
      description: form.description.trim(),
    });
    onClose();
  };

  return createPortal(
    <div className="cal-modal-backdrop" onMouseDown={onClose}>
      <form
        className="cal-modal"
        onMouseDown={(e) => e.stopPropagation()}
        onSubmit={submit}
        role="dialog"
        aria-modal="true"
      >
        <header className="cal-modal-head">
          <div>
            <h3 className="cal-modal-title">
              <span className="cal-dot" style={{ background: activeColor }} />
              {event ? t('calendar.dialog.editTitle') : t('calendar.dialog.createTitle')}
            </h3>
            <p className="cal-modal-sub">
              {event ? t('calendar.dialog.editSub') : t('calendar.dialog.createSub')}
            </p>
          </div>
          <button type="button" className="cal-icon-btn" onClick={onClose} title={t('calendar.dialog.close')}>
            <Icon name="x" size={18} />
          </button>
        </header>

        <label className="cal-field">
          <span className="cal-field-label">
            <Icon name="file-text" size={14} />
            {t('calendar.dialog.name')}
          </span>
          <input
            value={form.title}
            onChange={(e) => set('title', e.target.value)}
            placeholder={t('calendar.dialog.namePlaceholder')}
            autoFocus
          />
        </label>

        <div className="cal-field">
          <span className="cal-field-label">
            <Icon name="tag" size={14} />
            {t('calendar.dialog.calendar')}
          </span>
          <Select
            value={form.calendarId}
            options={calendarOptions}
            onChange={(value) => set('calendarId', value)}
            renderValue={(option) => (
              <span className="cal-select-value">
                <span className="cal-dot" style={{ background: option?.color }} />
                {option?.label}
              </span>
            )}
          />
        </div>

        <div className="cal-field-row">
          <div className="cal-field">
            <span className="cal-field-label">
              <Icon name="calendar" size={14} />
              {t('calendar.dialog.date')}
            </span>
            <DateField value={form.date} onChange={(date) => set('date', date)} locale={locale} />
          </div>

          <div className="cal-field">
            <span className="cal-field-label">
              <Icon name="clock" size={14} />
              {t('calendar.dialog.time')}
            </span>
            <Select
              value={form.time}
              options={TIME_OPTIONS.map((time) => ({ value: time, label: time }))}
              onChange={(value) => set('time', value)}
              className={form.allDay ? 'disabled' : ''}
            />
          </div>
        </div>

        <div className="cal-field-row">
          <div className="cal-field">
            <span className="cal-field-label">{t('calendar.dialog.duration')}</span>
            <Select
              value={form.allDay ? 1440 : form.duration}
              options={durations}
              onChange={(value) => {
                set('duration', value);
                set('allDay', value === 1440);
              }}
            />
          </div>

          <div className="cal-field">
            <span className="cal-field-label">{t('calendar.dialog.options')}</span>
            <div className="cal-toggles">
              <button
                type="button"
                className={`cal-toggle${form.allDay ? ' on' : ''}`}
                onClick={() => set('allDay', !form.allDay)}
              >
                <span className="cal-toggle-track"><span className="cal-toggle-knob" /></span>
                {t('calendar.dialog.allDay')}
              </button>
              <button
                type="button"
                className={`cal-toggle${form.reminder ? ' on' : ''}`}
                onClick={() => set('reminder', !form.reminder)}
              >
                <span className="cal-toggle-track"><span className="cal-toggle-knob" /></span>
                {t('calendar.dialog.reminder')}
              </button>
            </div>
          </div>
        </div>

        <label className="cal-field">
          <span className="cal-field-label">
            <Icon name="map-pin" size={14} />
            {t('calendar.dialog.location')}
          </span>
          <input
            value={form.location}
            onChange={(e) => set('location', e.target.value)}
            placeholder={t('calendar.dialog.locationPlaceholder')}
          />
        </label>

        <div className="cal-field">
          <span className="cal-field-label">
            <Icon name="users" size={14} />
            {t('calendar.dialog.attendees')}
          </span>
          <div className="cal-attendees-row">
            <input
              value={attendeeDraft}
              onChange={(e) => setAttendeeDraft(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === 'Enter') {
                  e.preventDefault();
                  addAttendee();
                }
              }}
              placeholder={t('calendar.dialog.attendeePlaceholder')}
            />
            <button type="button" className="cal-secondary-btn" onClick={addAttendee}>
              {t('calendar.dialog.add')}
            </button>
          </div>
          {form.attendees.length > 0 && (
            <div className="cal-attendee-chips">
              {form.attendees.map((name) => (
                <button
                  key={name}
                  type="button"
                  className="cal-attendee-chip"
                  onClick={() => set('attendees', form.attendees.filter((a) => a !== name))}
                >
                  {name}
                  <Icon name="x" size={12} />
                </button>
              ))}
            </div>
          )}
        </div>

        <label className="cal-field">
          <span className="cal-field-label">{t('calendar.dialog.description')}</span>
          <textarea
            rows={3}
            value={form.description}
            onChange={(e) => set('description', e.target.value)}
            placeholder={t('calendar.dialog.descriptionPlaceholder')}
          />
        </label>

        <footer className="cal-modal-foot">
          {event && (
            <button
              type="button"
              className="cal-delete-btn"
              onClick={() => { onDelete(event.id); onClose(); }}
            >
              <Icon name="trash-2" size={16} />
              {t('calendar.dialog.delete')}
            </button>
          )}
          <button type="submit" className="cal-primary-btn cal-modal-submit" disabled={!form.title.trim()}>
            {event ? t('calendar.dialog.save') : t('calendar.dialog.create')}
          </button>
          <button type="button" className="cal-secondary-btn" onClick={onClose}>
            {t('calendar.dialog.cancel')}
          </button>
        </footer>
      </form>
    </div>,
    document.body
  );
};

export default EventDialog;
